import React from "react";

import { mdiCheckBold } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import { Controller, useFormContext } from "react-hook-form";

interface StreamFormatCheckboxProps {
  title: string;
  subtitle: string;
  description: string;
  multiplier?: number;
  className?: string;
  name: string;
}

function StreamFormatCheckbox({
  title,
  subtitle,
  description,
  multiplier,
  className,
  name,
}: StreamFormatCheckboxProps): React.JSX.Element {
  const { control } = useFormContext();
  const getButtonClassNames = (isSelected: boolean): string =>
    classNames(
      "group flex flex-col rounded-lg border-2 p-[0.78125rem] text-left transition-all gap-[0.46875rem] cursor-pointer",
      {
        "shadow-sm border-primary bg-primary/5": isSelected,
        "border-border bg-card hover:border-border": !isSelected,
      },
      className,
    );

  const titleClassNames = (isSelected: boolean): string =>
    classNames("text-[0.9375rem] font-semibold leading-tight", {
      "text-primary": isSelected,
      "text-foreground": !isSelected,
    });

  const checkBoxClassNames = (isSelected: boolean): string =>
    classNames(
      "flex size-4 items-center justify-center rounded-md border-2 transition-all",
      {
        "shadow-lg border-primary bg-primary shadow-primary/20": isSelected,
        "border-border": !isSelected,
      },
    );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }): React.JSX.Element => (
        <button
          className={getButtonClassNames(field.value)}
          onClick={(): void => field.onChange(!field.value)}
          type="button"
        >
          <div className="mb-[0.46875rem] flex items-start justify-between">
            <div className="flex flex-col gap-0.5">
              <span className={titleClassNames(field.value)}>{title}</span>
              <span className="text-muted-foreground font-mono text-[0.625rem]">
                {subtitle}
              </span>
            </div>
            <div className={checkBoxClassNames(field.value)}>
              {field.value ? (
                <Icon
                  className="text-primary-foreground"
                  path={mdiCheckBold}
                  size={1}
                />
              ) : null}
            </div>
          </div>
          <div className="mt-auto flex justify-between gap-1.25">
            <p className="text-muted-foreground text-[0.703125rem] leading-tight font-medium">
              {description}
            </p>
            {multiplier !== undefined && (
              <span className="bg-muted text-muted-foreground self-start rounded px-1 py-0.5 font-mono text-[0.703125rem]">
                x{multiplier}
              </span>
            )}
          </div>
        </button>
      )}
    />
  );
}

export default StreamFormatCheckbox;
